import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Helper functions for Local Storage Auth
const getLocalUsers = () => {
  try {
    return JSON.parse(localStorage.getItem('local_users') || '[]');
  } catch (e) {
    console.error('Error parsing local_users:', e);
    return [];
  }
};

const saveLocalUsers = (users) => {
  try {
    localStorage.setItem('local_users', JSON.stringify(users));
  } catch (e) {
    console.error('Error saving local_users:', e);
  }
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  function signup(email, password, isLocal = false) {
    if (isLocal) {
      try {
        const users = getLocalUsers();
        const exists = users.some(u => u.email.toLowerCase() === email.toLowerCase());
        if (exists) {
          return Promise.reject(new Error('An account with this email already exists locally.'));
        }
        
        const newUser = {
          uid: 'local-' + Math.random().toString(36).substring(2, 11),
          email: email,
          password: password, // For mock authentication
          displayName: email.split('@')[0],
          photoURL: '',
          metadata: {
            creationTime: new Date().toISOString(),
            lastSignInTime: new Date().toISOString()
          },
          isLocal: true
        };
        
        users.push(newUser);
        saveLocalUsers(users);
        
        localStorage.setItem('local_user_session', JSON.stringify(newUser));
        setCurrentUser(newUser);
        return Promise.resolve(newUser);
      } catch (err) {
        return Promise.reject(err);
      }
    } else {
      return createUserWithEmailAndPassword(auth, email, password);
    }
  }

  function login(email, password, isLocal = false) {
    if (isLocal) {
      try {
        const users = getLocalUsers();
        const user = users.find(u => u.email.toLowerCase() === email.toLowerCase());
        
        if (!user) {
          return Promise.reject(new Error('Local account not found. Please sign up or switch to Cloud mode.'));
        }
        
        if (user.password !== password) {
          return Promise.reject(new Error('Incorrect password. Please try again.'));
        }
        
        // Update last sign in time
        user.metadata.lastSignInTime = new Date().toISOString();
        const updatedUsers = users.map(u => u.uid === user.uid ? user : u);
        saveLocalUsers(updatedUsers);
        
        localStorage.setItem('local_user_session', JSON.stringify(user));
        setCurrentUser(user);
        return Promise.resolve(user);
      } catch (err) {
        return Promise.reject(err);
      }
    } else {
      return signInWithEmailAndPassword(auth, email, password);
    }
  }

  async function logout() {
    try {
      localStorage.removeItem('local_user_session');
      setCurrentUser(null);
      await signOut(auth);
    } catch (error) {
      console.warn('Firebase signOut failed (likely not authenticated on cloud):', error.message);
    }
  }

  // Function to update user profile (displayName, photoURL)
  async function updateUserProfile(profileData) {
    if (currentUser && currentUser.isLocal) {
      try {
        const updatedUser = { ...currentUser, ...profileData };
        setCurrentUser(updatedUser);
        localStorage.setItem('local_user_session', JSON.stringify(updatedUser));
        
        // Update user in local database registry
        const users = getLocalUsers();
        const updatedUsers = users.map(u => u.uid === currentUser.uid ? { ...u, ...profileData } : u);
        saveLocalUsers(updatedUsers);
        return Promise.resolve();
      } catch (err) {
        console.error('Error updating local profile:', err);
        return Promise.reject(err);
      }
    }

    if (auth.currentUser) {
      try {
        await updateProfile(auth.currentUser, profileData);
        // Update currentUser state locally to reflect changes immediately
        setCurrentUser(prevUser => ({ ...prevUser, ...profileData }));
      } catch (error) {
        console.error("Error updating profile in AuthContext:", error);
        throw error; // Re-throw to be caught by the calling component
      }
    } else {
      throw new Error("No user currently signed in to update profile.");
    }
  }

  useEffect(() => {
    // Check if there is an active local user session
    const localSession = localStorage.getItem('local_user_session');
    if (localSession) {
      try {
        const user = JSON.parse(localSession);
        setCurrentUser(user);
        setLoading(false);
        return; // Skip Firebase subscriber for this local session
      } catch (e) {
        console.error('Failed to parse local user session, clearing...', e);
        localStorage.removeItem('local_user_session');
      }
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    login,
    signup,
    logout,
    updateUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
